/**
 * Design system: "A+" (BizNas Design System).
 *
 * Calm, exact and bank-like: a deep navy brand colour used sparingly, neutral
 * surfaces, and figures set in tabular IBM Plex so columns line up. Status is
 * always a word plus a tint, never colour alone, and Dr/Cr are written out
 * rather than told apart by teal and red.
 *
 * Two themes share one set of token names. The hex values live in `palettes`
 * (MUI needs real colours to derive hover and contrast shades); everything a
 * screen paints through `sx` or `style` uses `c`, whose values are CSS
 * variables, so a screen follows the light/dark switch without re-rendering
 * its own colours. The variables are declared in index.css.
 */
import { createTheme, type Theme } from '@mui/material/styles'

export type ThemeMode = 'light' | 'dark'

/** Token values per theme. Keep in step with index.css. */
export const palettes: Record<ThemeMode, Record<string, string>> = {
  light: {
    surfacePage: '#F5F6F8', surface: '#FFFFFF', surfaceSunken: '#F8F9FB',
    border: '#E3E6EB', borderStrong: '#D5DAE1', divider: '#EEF0F3',
    ink: '#16202E', inkSecondary: '#3A4554', inkMuted: '#5B6675',
    primary: '#1E3A5F', onPrimary: '#FFFFFF', primarySoft: '#E8EEF6', primaryInk: '#1E3A5F',
    focusRing: '#2F6FB5',
    successInk: '#0B6B4D', successSoft: '#E6F4EE',
    warning: '#B86A12', warningInk: '#8A4B08', warningSoft: '#FDF1E3',
    dangerInk: '#B42318', dangerSoft: '#FDECEA',
  },
  dark: {
    surfacePage: '#0D1117', surface: '#151A22', surfaceSunken: '#1B222C',
    border: '#262E3A', borderStrong: '#333C4A', divider: '#1F2630',
    ink: '#E6EAF0', inkSecondary: '#C7CEDA', inkMuted: '#98A2B3',
    primary: '#3B6CA8', onPrimary: '#FFFFFF', primarySoft: '#1A2A40', primaryInk: '#8DB4E2',
    focusRing: '#8DB4E2',
    successInk: '#6EE7B7', successSoft: '#0F2A22',
    warning: '#F59E0B', warningInk: '#FBBF24', warningSoft: '#2E2210',
    dangerInk: '#FCA5A5', dangerSoft: '#2C1414',
  },
}

/**
 * Colours for `sx` and inline styles, as CSS variables.
 *
 * The short legacy names are kept so existing screens did not all have to
 * change at once; each now points at the design-system token it means:
 * teal (positive / brand) -> primary-ink, gold (needs attention) -> warning,
 * brick (negative / error) -> danger.
 */
export const c = {
  // Design-system names
  surfacePage:  'var(--surface-page)',
  surface:      'var(--surface)',
  surfaceSunken:'var(--surface-sunken)',
  border:       'var(--border)',
  borderStrong: 'var(--border-strong)',
  divider:      'var(--divider)',
  ink:          'var(--ink)',
  inkSecondary: 'var(--ink-secondary)',
  inkMuted:     'var(--ink-muted)',
  primary:      'var(--primary)',
  onPrimary:    'var(--on-primary)',
  primarySoft:  'var(--primary-soft)',
  primaryInk:   'var(--primary-ink)',
  successInk:   'var(--success-ink)',
  successSoft:  'var(--success-soft)',
  warning:      'var(--warning)',
  warningInk:   'var(--warning-ink)',
  warningSoft:  'var(--warning-soft)',
  dangerInk:    'var(--danger-ink)',
  dangerSoft:   'var(--danger-soft)',
  chart1:       'var(--chart-1)',
  chart2:       'var(--chart-2)',
}

/**
 * Colours that do not follow the light/dark switch: the dark login panel and
 * the dark top bar. They are always navy, in both themes.
 */
export const chrome = {
  bg:     '#16202E',
  line:   '#26324A',
  text:   '#E6EAF0',
  muted:  '#A3ADBD',
  accent: '#8DB4E2',
}

export const tokens = {
  sans: "'IBM Plex Sans', 'IBM Plex Sans Arabic', system-ui, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
  rowHeight: 38,
}

export function buildTheme(direction: 'ltr' | 'rtl', mode: ThemeMode = 'light'): Theme {
  const p = palettes[mode]
  const body = direction === 'rtl'
    ? "'IBM Plex Sans Arabic', 'IBM Plex Sans', system-ui, sans-serif"
    : "'IBM Plex Sans', 'IBM Plex Sans Arabic', system-ui, -apple-system, sans-serif"

  return createTheme({
    direction,
    palette: {
      mode,
      primary:   { main: p.primary, contrastText: p.onPrimary },
      secondary: { main: p.warningInk },
      error:     { main: p.dangerInk },
      warning:   { main: p.warning },
      success:   { main: p.successInk },
      background:{ default: p.surfacePage, paper: p.surface },
      text:      { primary: p.ink, secondary: p.inkMuted },
      divider:   p.border,
    },
    typography: {
      fontFamily: body,
      fontSize: 14,
      h6: { fontWeight: 600, letterSpacing: '-0.01em' },
      subtitle2: { fontWeight: 600 },
      overline: {
        fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
        textTransform: 'uppercase', lineHeight: 1.45,
      },
      button: { textTransform: 'none', fontWeight: 500, letterSpacing: 0 },
    },
    shape: { borderRadius: 6 },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&:focus-visible': { outline: `2px solid ${p.focusRing}`, outlineOffset: 2 },
          },
          containedPrimary: { fontWeight: 600 },
          outlined: { borderColor: p.borderStrong },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { '&:focus-visible': { outline: `2px solid ${p.focusRing}`, outlineOffset: 1 } },
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: p.surface,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: p.borderStrong },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: p.primaryInk, borderWidth: 1,
            },
            '&.Mui-focused': {
              boxShadow: mode === 'light'
                ? '0 0 0 3px rgba(30,58,95,0.15)'
                : '0 0 0 3px rgba(141,180,226,0.25)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: p.border },
          rounded: { borderRadius: 10 },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${p.borderStrong}`,
            boxShadow: mode === 'light'
              ? '0 8px 24px rgba(22,32,46,0.14)'
              : '0 12px 32px rgba(0,0,0,0.5)',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            transition: 'background-color .12s ease',
            '&:hover': { backgroundColor: p.primarySoft },
            '&:focus-visible': { outline: `2px solid ${p.focusRing}`, outlineOffset: -2 },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500, letterSpacing: 0, borderRadius: 999 },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: mode === 'light' ? '#16202E' : '#E6EAF0',
                     color: mode === 'light' ? '#FFFFFF' : '#16202E', fontSize: 12 },
        },
      },
    },
  })
}

/** Money for display. Never parse money into a number for arithmetic. */
export function fmtMoney(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—'
  const n = typeof v === 'number' ? v : Number(v)
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })
}
